import React, { useState, Children, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircleIcon, TrendingUpIcon, UsersIcon, BookOpenIcon, ShieldCheckIcon, MonitorIcon, SearchIcon, MessageCircleIcon, FileTextIcon, DollarSignIcon, CalendarIcon, StarIcon, SmartphoneIcon, LaptopIcon, ZapIcon, TargetIcon, AwardIcon, PhoneIcon, MapPinIcon, RocketIcon, ShieldIcon, HeadphonesIcon, SparklesIcon, ReceiptIcon, LayoutDashboardIcon, BotIcon, BarChart3Icon } from 'lucide-react';
import { TimelineResultsSection } from '../components/TimelineResultsSection';
import { useAuth } from '../contexts/AuthContext';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
// Accordion Item Component
function AccordionItem({
  benefit,
  index,
  isDefaultOpen
}: {
  benefit: {
    icon: ComponentType<{
      className?: string;
    }>;
    title: string;
    shortDesc: string;
    fullDesc: string;
    color: string;
    bgColor: string;
  };
  index: number;
  isDefaultOpen: boolean;
}) {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const Icon = benefit.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className="group" data-id="element-7583">
      <button onClick={() => setIsOpen(!isOpen)} className={`w-full text-left transition-all duration-300 rounded-2xl border-2 overflow-hidden ${isOpen ? 'border-[#357BEB] shadow-lg' : 'border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md'}`} data-id="element-7584">
        {/* Header */}
        <div className={`p-6 flex items-center justify-between transition-colors duration-300 ${isOpen ? benefit.bgColor : 'bg-white'}`} data-id="element-7585">
          <div className="flex items-center space-x-4 flex-1" data-id="element-7586">
            {/* Icon */}
            <motion.div animate={{
            scale: isOpen ? 1.1 : 1,
            rotate: isOpen ? 360 : 0
          }} transition={{
            duration: 0.5
          }} className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${benefit.color} shadow-md`} data-id="element-7587">
              <Icon className="w-7 h-7 text-white" data-id="element-7588" />
            </motion.div>

            {/* Title and Short Description */}
            <div className="flex-1 min-w-0" data-id="element-7589">
              <h3 className="text-xl font-bold text-[#132C53] mb-1" data-id="element-7590">
                {benefit.title}
              </h3>
              <p className="text-sm text-gray-600" data-id="element-7591">{benefit.shortDesc}</p>
            </div>
          </div>

          {/* Expand/Collapse Icon */}
          <motion.div animate={{
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.3
        }} className="flex-shrink-0 ml-4" data-id="element-7592">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-[#357BEB]' : 'bg-gray-200 group-hover:bg-gray-300'}`} data-id="element-7593">
              <svg className={`w-5 h-5 transition-colors ${isOpen ? 'text-white' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-7594">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-7595" />
              </svg>
            </div>
          </motion.div>
        </div>

        {/* Expanded Content */}
        <motion.div initial={false} animate={{
        height: isOpen ? 'auto' : 0,
        opacity: isOpen ? 1 : 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-7596">
          <div className={`p-6 pt-0 ${benefit.bgColor}`} data-id="element-7597">
            <div className="pl-[72px]" data-id="element-7598">
              <p className="text-gray-700 leading-relaxed" data-id="element-7599">
                {benefit.fullDesc}
              </p>
            </div>
          </div>
        </motion.div>
      </button>
    </motion.div>;
}
export function Career3() {
  const {
    isAuthenticated
  } = useAuth();
  // Steps for "Avem Rețeta Succesului" section
  const steps = [{
    number: '01',
    title: 'Înregistrează-te',
    description: 'Creează-ți contul în platformă. Este primul pas către independența ta financiară.'
  }, {
    number: '02',
    title: 'Învață "Rețeta"',
    description: 'Parcurge cursurile noastre interactive. Îți explicăm exact ce ai de făcut, pas cu pas.'
  }, {
    number: '03',
    title: 'Aplică și Câștigă',
    description: 'Folosește instrumentele noastre digitale pentru a găsi clienți și a încheia tranzacții.'
  }];
  return <div className="w-full min-h-screen bg-white font-sans text-[#132C53]" data-id="element-7600">
      {/* 1. HERO SECTION - MOBILE OPTIMIZED */}
      <section className="relative bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white pt-20 pb-12 sm:pt-24 sm:pb-16 md:pt-32 md:pb-24 overflow-hidden" data-id="element-7601">
        {/* Background pattern overlay */}
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-7602"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-7603">
          <div className="text-center max-w-4xl mx-auto" data-id="element-7604">
            {/* Badge */}
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full mb-4 sm:mb-6 border border-white/20" data-id="element-7605">
              <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 fill-current" data-id="element-7606" />
              <span className="text-xs sm:text-sm font-medium text-white" data-id="element-7607">
                Începe cariera ta în imobiliare astăzi
              </span>
            </motion.div>

            <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight mb-4 sm:mb-6 text-white leading-tight" data-id="element-7608">
              Devino Agent Imobiliar.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-7609">
                Începe Azi.
              </span>
            </motion.h1>

            <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2" data-id="element-7610">
              La CautLocuință.ro ai rețeta completă, suport 1 la 1 și cea mai
              simplă platformă digitală pentru agenți imobiliari. Nu ai nevoie
              de experiență. Ai nevoie doar să începi.
            </motion.p>

            {/* 3 Steps Block - MOBILE OPTIMIZED */}
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-7611">
              {[{
              step: 1,
              title: 'Apeși pe «Devino Agent»',
              desc: 'Pornești procesul în câteva secunde.'
            }, {
              step: 2,
              title: 'Creezi contul',
              desc: 'Completarea durează sub 60 de secunde.'
            }, {
              step: 3,
              title: 'Începi primul curs',
              desc: 'Primești acces imediat la Cursul 1 + resurse.'
            }].map((item, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-all" data-id="element-7612">
                  <div className="flex items-start space-x-3 mb-2" data-id="element-7613">
                    <div className="w-6 h-6 flex-shrink-0 rounded-full bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white flex items-center justify-center text-xs font-bold" data-id="element-7614">
                      {item.step}
                    </div>
                    <div className="flex-1 text-left" data-id="element-7615">
                      <h3 className="font-bold text-sm md:text-base text-white leading-tight mb-1" data-id="element-7616">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-300 leading-relaxed" data-id="element-7617">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="flex flex-col items-center px-4" data-id="element-7618">
              <Link to="/auth/register" className="inline-flex items-center justify-center px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-bold rounded-2xl text-white bg-gradient-to-r from-[#357BEB] to-[#21A366] hover:shadow-2xl hover:scale-105 transition-all duration-300 w-full sm:w-auto group" data-id="element-7619">
                Devino Agent
                <ArrowRightIcon className="ml-2 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" data-id="element-7620" />
              </Link>
              <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-400 px-2" data-id="element-7621">
                Creezi contul în 60 secunde. Acces instant la primul curs.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Decorative blur elements */}
        <div className="absolute -top-10 -right-10 w-48 h-48 sm:w-64 sm:h-64 bg-[#357BEB] rounded-full blur-3xl opacity-20" data-id="element-7622"></div>
        <div className="absolute -bottom-10 -left-10 w-48 h-48 sm:w-64 sm:h-64 bg-[#21A366] rounded-full blur-3xl opacity-20" data-id="element-7623"></div>
      </section>

      {/* TIMELINE RESULTS SECTION */}
      <TimelineResultsSection data-id="element-7624" />

      {/* 2. WHY CLC SECTION - MOBILE OPTIMIZED WITH DESKTOP TOOLTIPS */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#F9FAFB] relative overflow-hidden" data-id="element-7625">
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(#357BEB 1px, transparent 1px), linear-gradient(90deg, #357BEB 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} data-id="element-7626"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-7627">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12 sm:mb-16 md:mb-20" data-id="element-7628">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-[#132C53] px-4" data-id="element-7629">
              De ce să devii agent în ecosistemul CLC
            </h2>
            <p className="text-lg sm:text-xl text-[#357BEB] font-medium" data-id="element-7630">
              Simplu. Pentru că funcționează.
            </p>
          </motion.div>

          {/* Timeline Flow - RESTORED DESKTOP ANIMATION */}
          <div className="relative" data-id="element-7631">
            {/* Connecting curved path - Desktop */}
            <svg className="hidden lg:block absolute top-1/2 left-0 w-full h-32 -translate-y-1/2 pointer-events-none" preserveAspectRatio="none" data-id="element-7632">
              <defs data-id="element-7633">
                <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-7634">
                  <stop offset="0%" stopColor="#357BEB" data-id="element-7635" />
                  <stop offset="100%" stopColor="#21A366" data-id="element-7636" />
                </linearGradient>
              </defs>
              <path d="M 0 64 Q 200 20, 400 64 T 800 64 T 1200 64" stroke="url(#pathGradient)" strokeWidth="3" fill="none" opacity="0.3" data-id="element-7637" />
              {/* Animated dot */}
              <circle r="6" fill="#357BEB" opacity="0.6" data-id="element-7638">
                <animateMotion dur="8s" repeatCount="indefinite" path="M 0 64 Q 200 20, 400 64 T 800 64 T 1200 64" data-id="element-7639" />
              </circle>
            </svg>

            {/* Benefits as nodes - RESTORED DESKTOP TOOLTIPS */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 sm:gap-8 lg:gap-4" data-id="element-7640">
              {[{
              icon: TargetIcon,
              title: 'Proces Clar',
              text: 'Proces pas cu pas — știi exact ce ai de făcut',
              color: 'from-[#357BEB] to-[#2868d8]'
            }, {
              icon: BookOpenIcon,
              title: 'Fără Experiență',
              text: 'Fără experiență necesară — înveți de la zero',
              color: 'from-[#2868d8] to-[#1e5bb8]'
            }, {
              icon: TrendingUpIcon,
              title: 'Rezultate Rapide',
              text: 'Primele tranzacții în 30–60 de zile',
              color: 'from-[#1e5bb8] to-[#1a4d9e]'
            }, {
              icon: UsersIcon,
              title: 'Suport 1-la-1',
              text: 'Suport individual 1 la 1',
              color: 'from-[#1a4d9e] to-[#21A366]'
            }, {
              icon: CalendarIcon,
              title: 'Flexibilitate',
              text: 'Fără program fix / fără prezență fizică',
              color: 'from-[#21A366] to-[#1d8f59]'
            }, {
              icon: DollarSignIcon,
              title: 'Venituri Masive',
              text: 'Comisioane competitive și oportunități nelimitate de câștig',
              color: 'from-[#1d8f59] to-[#21A366]'
            }].map((benefit, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              scale: 0.8
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1,
              duration: 0.5
            }} className="flex flex-col items-center relative group" data-id="element-7641">
                  {/* Node circle */}
                  <motion.div whileHover={{
                scale: 1.15
              }} className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br ${benefit.color} flex items-center justify-center shadow-lg cursor-pointer relative z-10 border-4 border-white`} data-id="element-7642">
                    <benefit.icon className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" data-id="element-7643" />

                    {/* Pulse animation */}
                    <motion.div className={`absolute inset-0 rounded-full bg-gradient-to-br ${benefit.color} opacity-30`} animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.3, 0, 0.3]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: idx * 0.3
                }} data-id="element-7644" />
                  </motion.div>

                  {/* Short title below node */}
                  <p className="mt-3 sm:mt-4 text-xs sm:text-sm font-bold text-[#132C53] text-center" data-id="element-7645">
                    {benefit.title}
                  </p>

                  {/* Description - Always visible on mobile, tooltip on desktop */}
                  <p className="lg:hidden mt-2 text-xs text-gray-600 text-center leading-relaxed px-2" data-id="element-7646">
                    {benefit.text}
                  </p>

                  {/* Tooltip popup on hover - DESKTOP ONLY */}
                  <div className="hidden lg:block absolute top-full mt-8 left-1/2 -translate-x-1/2 w-64 bg-white rounded-2xl shadow-2xl p-6 border-2 border-gray-100 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none group-hover:pointer-events-auto z-20 transform group-hover:translate-y-0 translate-y-2" data-id="element-7647">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 bg-white border-l-2 border-t-2 border-gray-100 transform rotate-45" data-id="element-7648"></div>
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-4 mx-auto`} data-id="element-7649">
                      <benefit.icon className="w-6 h-6 text-white" data-id="element-7650" />
                    </div>
                    <h4 className="font-bold text-[#132C53] mb-2 text-center" data-id="element-7651">
                      {benefit.title}
                    </h4>
                    <p className="text-sm text-gray-600 text-center leading-relaxed" data-id="element-7652">
                      {benefit.text}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* Progress indicator */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.8
        }} className="mt-16 text-center" data-id="element-7653">
            <div className="flex justify-center items-center space-x-2" data-id="element-7654">
              {[0, 1, 2, 3, 4, 5].map(i => <motion.div key={i} className="w-2 h-2 rounded-full bg-[#357BEB]" animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.3
            }} data-id="element-7655" />)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* NEW SECTION - De ce Cautlocuință.ro - MOBILE OPTIMIZED */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-7656">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7657">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-12 sm:mb-16" data-id="element-7658">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#132C53] mb-3 sm:mb-4 px-4" data-id="element-7659">
              De ce Cautlocuință.ro?
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto px-4" data-id="element-7660">
              Oferim cel mai complet ecosistem de suport pentru agenții
              imobiliari
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12" data-id="element-7661">
            {[{
            icon: RocketIcon,
            title: 'Training complet',
            description: 'Masterclass profesionale și mentorat continuu pentru dezvoltare'
          }, {
            icon: UsersIcon,
            title: 'Leaduri garantate',
            description: 'Acces la baza noastră de clienți și proprietari verificați'
          }, {
            icon: ShieldIcon,
            title: 'Suport juridic',
            description: 'Echipă juridică dedicată pentru toate tranzacțiile tale'
          }, {
            icon: TargetIcon,
            title: 'Marketing profesional',
            description: 'Campanii de promovare și branding pentru proprietățile tale'
          }, {
            icon: HeadphonesIcon,
            title: 'Suport 24/7',
            description: 'Asistență tehnică și operațională oricând ai nevoie'
          }, {
            icon: TrendingUpIcon,
            title: 'Comisioane competitive',
            description: 'Sistem transparent de comisionare cu bonusuri de performanță'
          }].map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="bg-white rounded-xl p-5 sm:p-6 shadow-md hover:shadow-xl transition-shadow border-2 border-transparent hover:border-[#357BEB]" data-id="element-7662">
                <div className="w-12 h-12 rounded-xl bg-[#357BEB] bg-opacity-10 flex items-center justify-center mb-4" data-id="element-7663">
                  <item.icon className="w-6 h-6 text-[#357BEB]" data-id="element-7664" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-[#132C53] mb-2" data-id="element-7665">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-7666">{item.description}</p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-gradient-to-r from-[#357BEB] to-[#21A366] rounded-2xl p-6 sm:p-8 text-white text-center" data-id="element-7667">
            <h3 className="text-xl sm:text-2xl font-bold mb-3" data-id="element-7668">
              🎯 Totul pentru succesul tău
            </h3>
            <p className="text-base sm:text-lg text-gray-100 max-w-3xl mx-auto" data-id="element-7669">
              Nu ești singur în această călătorie. Avem toate resursele,
              instrumentele și expertiza necesare pentru a te transforma într-un
              agent de top.
            </p>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION - MOBILE OPTIMIZED */}
      <section className="bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12 sm:py-16 md:py-20 relative overflow-hidden" data-id="element-7670">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-100 rounded-full blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2" data-id="element-7671" />
        <div className="absolute bottom-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-green-100 rounded-full blur-3xl opacity-20 translate-x-1/2 translate-y-1/2" data-id="element-7672" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-7673">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-7674">
            {/* Left side - Interactive steps */}
            <div data-id="element-7675">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} data-id="element-7676">
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full mb-4 sm:mb-6 shadow-lg" data-id="element-7677">
                  <StarIcon className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-7678" />
                  <span className="text-xs sm:text-sm font-semibold" data-id="element-7679">
                    Sistemul nostru de succes
                  </span>
                </motion.div>

                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#132C53] mb-4 sm:mb-6 leading-tight" data-id="element-7680">
                  Avem Rețeta Succesului.{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-7681">
                    Trebuie doar să o aplici.
                  </span>
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-10 leading-relaxed" data-id="element-7682">
                  Am simplificat procesul de a deveni agent imobiliar. Nu te
                  aruncăm în piață fără direcție. Îți oferim o hartă clară,
                  instrumente digitale și suport constant.
                </p>

                <div className="space-y-4 sm:space-y-6" data-id="element-7683">
                  {steps.map((step, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }} className="relative bg-white rounded-2xl p-5 sm:p-6 shadow-md border-2 border-gray-100" data-id="element-7684">
                      <div className="flex items-start space-x-3 sm:space-x-4" data-id="element-7685">
                        <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#357BEB] to-[#21A366] text-white rounded-2xl flex items-center justify-center font-bold text-lg sm:text-xl shadow-lg" data-id="element-7686">
                          {step.number}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-7687">
                          <h3 className="text-lg sm:text-xl font-bold text-[#132C53] mb-2" data-id="element-7688">
                            {step.title}
                          </h3>
                          <p className="text-sm sm:text-base text-gray-600 leading-relaxed" data-id="element-7689">
                            {step.description}
                          </p>
                        </div>
                      </div>

                      {/* Animated progress bar */}
                      <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '100%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.8,
                    delay: index * 0.2
                  }} className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#357BEB] to-[#21A366] rounded-b-2xl" data-id="element-7690" />
                    </motion.div>)}
                </div>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.5
              }} className="mt-8 sm:mt-10" data-id="element-7691">
                  <Link to="/auth/register" className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold rounded-2xl text-white bg-gradient-to-r from-[#357BEB] to-[#21A366] hover:shadow-2xl hover:scale-105 transition-all duration-300 group w-full sm:w-auto" data-id="element-7692">
                    Începe Acum
                    <ArrowRightIcon className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-7693" />
                  </Link>
                  <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500" data-id="element-7694">
                    ✓ Fără experiență necesară • ✓ Suport complet • ✓ Start în
                    60 secunde
                  </p>
                </motion.div>
              </motion.div>
            </div>

            {/* Right side - Image with bottom card - MOBILE OPTIMIZED */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative mt-8 lg:mt-0" data-id="element-7695">
              {/* Main image container */}
              <div className="relative" data-id="element-7696">
                <div className="absolute inset-0 bg-gradient-to-br from-[#357BEB] to-[#21A366] rounded-3xl transform rotate-3 opacity-20" data-id="element-7697" />
                <img src="https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Team collaboration and training" className="relative rounded-3xl shadow-2xl w-full object-cover h-[400px] sm:h-[500px] md:h-[600px]" data-id="element-7698" />

                {/* Bottom overlay card - MOBILE OPTIMIZED */}
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.4
              }} className="absolute bottom-4 left-4 right-4 sm:bottom-8 sm:left-8 sm:right-8 bg-white/95 backdrop-blur-md p-4 sm:p-6 rounded-2xl shadow-2xl border-2 border-white/50" data-id="element-7699">
                  <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-7700">
                    <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0" data-id="element-7701">
                      <div className="flex -space-x-2 sm:-space-x-3 flex-shrink-0" data-id="element-7702">
                        {[{
                        name: 'Cătălin Stanescu',
                        img: 'https://i.pravatar.cc/100?img=12'
                      }, {
                        name: 'Simona Stancu',
                        img: 'https://i.pravatar.cc/100?img=47'
                      }, {
                        name: 'Alexandru Merdaru',
                        img: 'https://i.pravatar.cc/100?img=33'
                      }, {
                        name: 'Mai mulți agenți',
                        img: null
                      }].map((agent, i) => <motion.div key={i} initial={{
                        scale: 0
                      }} whileInView={{
                        scale: 1
                      }} viewport={{
                        once: true
                      }} transition={{
                        delay: 0.6 + i * 0.1,
                        type: 'spring'
                      }} className="relative" data-id="element-7703">
                            {agent.img ? <img src={agent.img} alt={agent.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-3 border-white shadow-lg object-cover" data-id="element-7704" /> : <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-3 border-white bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-lg" data-id="element-7705">
                                +
                              </div>}
                          </motion.div>)}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7706">
                        <p className="font-bold text-[#132C53] text-sm sm:text-base lg:text-lg truncate" data-id="element-7707">
                          Alătură-te echipei
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate" data-id="element-7708">
                          Intră în comunitatea de agenți CLC
                        </p>
                      </div>
                    </div>
                    <CheckCircleIcon className="w-6 h-6 sm:w-8 sm:h-8 text-[#21A366] flex-shrink-0" data-id="element-7709" />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. RECIPE SECTION - MOBILE OPTIMIZED */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-7710">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7711">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-6 sm:mb-8" data-id="element-7712">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-[#132C53] px-4" data-id="element-7713">
              Rețeta CLC: procesul care produce rezultate
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4" data-id="element-7714">
              Nu îți încerci norocul. Urmezi un sistem care deja funcționează în
              mii de tranzacții.
            </p>
          </motion.div>

          {/* Badge between title and cards */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="text-center mb-8 sm:mb-12" data-id="element-7715">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white px-4 py-2 sm:px-6 sm:py-3 rounded-full shadow-lg" data-id="element-7716">
              <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-7717" />
              <span className="text-sm sm:text-base font-semibold" data-id="element-7718">
                6 pași simpli către succesul tău
              </span>
            </div>
          </motion.div>

          {/* Flowing Cards Layout - MOBILE OPTIMIZED */}
          <div className="relative max-w-6xl mx-auto" data-id="element-7719">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-7720">
              {[{
              icon: SearchIcon,
              title: 'Prospectare simplificată',
              desc: 'Listă zilnică cu proprietari pregătiți de contactare.',
              color: 'from-[#357BEB] to-[#2868d8]',
              delay: 0
            }, {
              icon: MessageCircleIcon,
              title: 'Colaborare rapidă cu proprietarul',
              desc: 'Oferta CLC personalizată pentru colaborări',
              color: 'from-[#2868d8] to-[#1e5bb8]',
              delay: 0.1
            }, {
              icon: ZapIcon,
              title: 'Anunțuri generate automat',
              desc: 'Descriere, fotografii, AI tools, publicare pe platforme.',
              color: 'from-[#1e5bb8] to-[#1a4d9e]',
              delay: 0.2
            }, {
              icon: UsersIcon,
              title: 'Leaduri zilnice',
              desc: 'Liste de clienți zilnic cu care să tranzacționezi',
              color: 'from-[#1a4d9e] to-[#168f7e]',
              delay: 0.3
            }, {
              icon: CalendarIcon,
              title: 'Vizionări organizate',
              desc: 'Programări, statusuri, notițe, follow-up.',
              color: 'from-[#168f7e] to-[#1d8f59]',
              delay: 0.4
            }, {
              icon: DollarSignIcon,
              title: 'Tranzacție finalizată → comision încasat',
              desc: 'Proces clar, predictibil, repetabil.',
              color: 'from-[#1d8f59] to-[#21A366]',
              highlight: true,
              delay: 0.5
            }].map((step, idx) => <motion.div key={idx} variants={fadeInUp} transition={{
              delay: step.delay
            }} className={`
                    relative group
                    ${step.highlight ? 'md:col-span-2 lg:col-span-3' : ''}
                  `} data-id="element-7721">
                  <div className={`
                      h-full rounded-2xl sm:rounded-3xl p-6 sm:p-8 transition-all duration-500
                      ${step.highlight ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 shadow-xl hover:shadow-2xl' : 'bg-white border-2 border-gray-100 shadow-lg hover:shadow-xl hover:border-blue-200'}
                    `} data-id="element-7722">
                    {/* Icon with gradient background */}
                    <motion.div whileHover={{
                  scale: 1.1,
                  rotate: 5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} className={`
                        w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6
                        bg-gradient-to-br ${step.color} shadow-lg
                      `} data-id="element-7723">
                      <step.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" data-id="element-7724" />
                    </motion.div>

                    {/* Step number badge */}
                    <div className="absolute top-4 right-4 sm:top-6 sm:right-6" data-id="element-7725">
                      <div className={`
                        w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold
                        ${step.highlight ? 'bg-[#21A366] text-white' : 'bg-gray-100 text-gray-500'}
                      `} data-id="element-7726">
                        {idx + 1}
                      </div>
                    </div>

                    {/* Content */}
                    <div className={step.highlight ? 'md:flex md:items-center md:justify-between md:gap-8' : ''} data-id="element-7727">
                      <div className={step.highlight ? 'md:flex-1' : ''} data-id="element-7728">
                        <h3 className={`
                          text-lg sm:text-xl font-bold mb-2 sm:mb-3
                          ${step.highlight ? 'text-[#21A366] sm:text-2xl' : 'text-[#132C53]'}
                        `} data-id="element-7729">
                          {step.title}
                        </h3>
                        <p className={`
                          text-sm sm:text-base leading-relaxed
                          ${step.highlight ? 'text-gray-700 sm:text-lg' : 'text-gray-600'}
                        `} data-id="element-7730">
                          {step.desc}
                        </p>
                      </div>

                      {/* Success indicator for final step */}
                      {step.highlight && <motion.div initial={{
                    scale: 0
                  }} whileInView={{
                    scale: 1
                  }} transition={{
                    delay: 0.8,
                    type: 'spring'
                  }} className="mt-6 md:mt-0 flex justify-center" data-id="element-7731">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#21A366] flex items-center justify-center shadow-xl" data-id="element-7732">
                            <CheckCircleIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" data-id="element-7733" />
                          </div>
                        </motion.div>}
                    </div>

                    {/* Hover effect - subtle glow */}
                    <div className={`
                      absolute inset-0 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none
                      bg-gradient-to-br ${step.color}
                    `} style={{
                  mixBlendMode: 'overlay'
                }} data-id="element-7734" />
                  </div>

                  {/* Connecting arrow (not on last item, hidden on mobile) */}
                  {!step.highlight && <motion.div initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: step.delay + 0.3
              }} className="hidden lg:block absolute -bottom-4 left-1/2 -translate-x-1/2 z-10" data-id="element-7735">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-md`} data-id="element-7736">
                        <ArrowRightIcon className="w-4 h-4 text-white rotate-90" data-id="element-7737" />
                      </div>
                    </motion.div>}
                </motion.div>)}
            </motion.div>

            {/* Decorative elements */}
            <div className="absolute -top-10 -left-10 w-32 h-32 sm:w-40 sm:h-40 bg-blue-100 rounded-full blur-3xl opacity-30 pointer-events-none" data-id="element-7738" />
            <div className="absolute -bottom-10 -right-10 w-32 h-32 sm:w-40 sm:h-40 bg-green-100 rounded-full blur-3xl opacity-30 pointer-events-none" data-id="element-7739" />
          </div>
        </div>
      </section>

      {/* 5. SUPPORT SECTION - MOBILE OPTIMIZED */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#F9FAFB]" data-id="element-7740">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7741">
          <div className="text-center mb-12 sm:mb-16" data-id="element-7742">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-[#132C53] px-4" data-id="element-7743">
              Nu ești singur. Ai suport individual.
            </h2>
            <p className="text-base sm:text-lg text-gray-600 px-4" data-id="element-7744">
              Agenții CLC primesc suport real, nu doar training general.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6" data-id="element-7745">
            {[{
            title: 'Suport individual 1 la 1',
            icon: UsersIcon
          }, {
            title: 'Ajutor în primele apeluri',
            icon: PhoneIcon
          }, {
            title: 'Ghidare pentru anunțuri și vizionări',
            icon: MapPinIcon
          }, {
            title: 'Suport la contracte, negocieri și tranzacții',
            icon: FileTextIcon
          }, {
            title: 'Răspuns rapid în platformă',
            icon: MessageCircleIcon
          }, {
            title: 'Resurse și materiale marketing',
            icon: BookOpenIcon
          }].map((item, idx) => <motion.div key={idx} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="bg-white p-5 sm:p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center hover:shadow-md transition-all" data-id="element-7746">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-blue-50 flex items-center justify-center text-[#357BEB] mb-3 sm:mb-4" data-id="element-7747">
                  <item.icon className="w-6 h-6 sm:w-7 sm:h-7" data-id="element-7748" />
                </div>
                <h3 className="font-bold text-sm sm:text-base text-[#132C53]" data-id="element-7749">
                  {item.title}
                </h3>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. COURSE 1 SECTION - MOBILE OPTIMIZED */}
      <section className="py-12 sm:py-16 md:py-20 bg-white overflow-hidden" data-id="element-7750">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7751">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center" data-id="element-7752">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} data-id="element-7753">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-[#132C53]" data-id="element-7754">
                Începe simplu: Training 1 – Concept CLC
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-7755">
                Imediat după crearea contului intri în Training 1. În mai puțin
                de o oră înțelegi cum funcționează ecosistemul, ce rol ai ca
                agent, cum arată fluxul de tranzacție și cum poți genera primele
                rezultate rapid.
              </p>

              <ul className="space-y-3 sm:space-y-4" data-id="element-7756">
                {['Cum funcționează ecosistemul CLC', 'Ce înseamnă să fii agent CLC', 'Fluxul complet de tranzacție', 'Ce resurse ai la dispoziție 7/7', 'Cum generezi tranzacții în primele 30 zile'].map((item, idx) => <li key={idx} className="flex items-center space-x-3" data-id="element-7757">
                    <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#21A366] flex-shrink-0" data-id="element-7758" />
                    <span className="text-sm sm:text-base text-gray-700" data-id="element-7759">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} transition={{
            delay: 0.2
          }} className="relative" data-id="element-7760">
              {/* Realistic Course 1 Card Mockup - MOBILE OPTIMIZED */}
              <Link to={isAuthenticated ? '/curs/concept-clc' : '/auth/register'} className="block" data-id="element-7761">
                <div className="bg-gradient-to-br from-[#357BEB] to-[#2868d8] rounded-2xl p-5 sm:p-6 shadow-2xl text-white border-2 border-[#357BEB] relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-3xl hover:-translate-y-1 hover:border-[#21A366]" data-id="element-7762">
                  {/* Decorative blur */}
                  <div className="absolute top-0 right-0 w-48 h-48 sm:w-64 sm:h-64 bg-white opacity-5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" data-id="element-7763"></div>

                  <div className="relative z-10" data-id="element-7764">
                    {/* Status Badge */}
                    <div className="flex items-start justify-between mb-4" data-id="element-7765">
                      <div className="flex-1" data-id="element-7766">
                        <span className="inline-flex items-center space-x-1 bg-green-100 text-green-700 px-2.5 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-semibold mb-3" data-id="element-7767">
                          <CheckCircleIcon className="w-3 h-3" data-id="element-7768" />
                          <span data-id="element-7769">Disponibil acum</span>
                        </span>

                        <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-white" data-id="element-7770">
                          Training 1 — Concept CLC
                        </h3>

                        <p className="text-xs sm:text-sm text-gray-100 mb-3 sm:mb-4 leading-relaxed" data-id="element-7771">
                          Înțelegerea conceptului Cautlocuință.ro, a structurii
                          agenției și a ecosistemului profesional.
                        </p>
                      </div>
                    </div>

                    {/* Course Info */}
                    <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-4 text-xs sm:text-sm text-gray-200" data-id="element-7772">
                      <div className="flex items-center space-x-1" data-id="element-7773">
                        <BookOpenIcon className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-7774" />
                        <span data-id="element-7775">2 module • 6 lecții</span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4 sm:mb-6" data-id="element-7776">
                      <div className="flex items-center justify-between text-xs text-gray-200 mb-2" data-id="element-7777">
                        <span data-id="element-7778">Progres</span>
                        <span data-id="element-7779">0%</span>
                      </div>
                      <div className="bg-white bg-opacity-20 rounded-full h-2 overflow-hidden" data-id="element-7780">
                        <motion.div initial={{
                        width: 0
                      }} whileInView={{
                        width: '0%'
                      }} transition={{
                        duration: 1,
                        delay: 0.5
                      }} className="bg-white h-full rounded-full" data-id="element-7781" />
                      </div>
                    </div>

                    {/* CTA Button */}
                    <div className="flex items-center justify-between text-white" data-id="element-7782">
                      <span className="font-semibold text-base sm:text-lg" data-id="element-7783">
                        {isAuthenticated ? 'Deschide training-ul' : 'Creează cont și începe'}
                      </span>
                      <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-7784" />
                    </div>
                  </div>

                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-300 rounded-2xl" data-id="element-7785" />
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. PLATFORM SECTION - MOBILE OPTIMIZED */}
      <section className="py-12 sm:py-16 md:py-20 bg-white" data-id="element-7786">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7787">
          <div className="text-center mb-12 sm:mb-16" data-id="element-7788">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-[#132C53] px-4" data-id="element-7789">
              Platforma digitală care îți face munca mai ușoară
            </h2>
            <p className="text-base sm:text-lg text-gray-600 px-4" data-id="element-7790">
              Tot workflow-ul tău este într-un singur loc, creat special pentru
              agenți imobiliari.
            </p>
          </div>

          {/* Interactive Module Cards with Icons - MOBILE OPTIMIZED */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 mb-12 sm:mb-16" data-id="element-7791">
            {[{
            name: 'Prospectare',
            icon: SearchIcon,
            description: 'Liste zilnice cu proprietari pregătiți',
            color: 'from-blue-500 to-blue-600'
          }, {
            name: 'Anunțuri & AI',
            icon: SparklesIcon,
            description: 'Generare automată cu inteligență artificială',
            color: 'from-purple-500 to-purple-600'
          }, {
            name: 'Leaduri',
            icon: UsersIcon,
            description: 'Clienți verificați gata de contactare',
            color: 'from-green-500 to-green-600'
          }, {
            name: 'Vizionări',
            icon: CalendarIcon,
            description: 'Programări și follow-up organizate',
            color: 'from-orange-500 to-orange-600'
          }, {
            name: 'Contracte',
            icon: FileTextIcon,
            description: 'Semnare electronică și stocare sigură',
            color: 'from-red-500 to-red-600'
          }, {
            name: 'Tranzacții',
            icon: DollarSignIcon,
            description: 'Urmărire completă până la finalizare',
            color: 'from-emerald-500 to-emerald-600'
          }, {
            name: 'Facturare',
            icon: ReceiptIcon,
            description: 'Generare automată de facturi și rapoarte',
            color: 'from-cyan-500 to-cyan-600'
          }, {
            name: 'Dashboard',
            icon: LayoutDashboardIcon,
            description: 'Statistici și performanță în timp real',
            color: 'from-indigo-500 to-indigo-600'
          }, {
            name: 'Cursuri',
            icon: BookOpenIcon,
            description: 'Training continuu și resurse educaționale',
            color: 'from-pink-500 to-pink-600'
          }, {
            name: 'Suport AI',
            icon: BotIcon,
            description: 'Asistent inteligent disponibil 24/7',
            color: 'from-violet-500 to-violet-600'
          }].map((module, idx) => <motion.div key={idx} variants={fadeInUp} transition={{
            delay: idx * 0.05
          }} className="group relative" data-id="element-7792">
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-gray-100 hover:border-[#357BEB] transition-all duration-300 hover:shadow-xl cursor-pointer h-full flex flex-col items-center" data-id="element-7793">
                  {/* Icon */}
                  <motion.div whileHover={{
                scale: 1.1,
                rotate: 5
              }} transition={{
                type: 'spring',
                stiffness: 300
              }} className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center mb-3 sm:mb-4 shadow-lg`} data-id="element-7794">
                    <module.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" data-id="element-7795" />
                  </motion.div>

                  {/* Module Name */}
                  <p className="font-bold text-xs sm:text-sm text-[#132C53] text-center mb-2" data-id="element-7796">
                    {module.name}
                  </p>

                  {/* Description - visible on desktop hover, hidden on mobile */}
                  <p className="hidden md:block text-xs text-gray-600 text-center leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7797">
                    {module.description}
                  </p>

                  {/* Hover glow effect */}
                  <div className={`absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br ${module.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none`} data-id="element-7798" />
                </div>
              </motion.div>)}
          </motion.div>

          {/* Enhanced Mockup Visual - MOBILE OPTIMIZED */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="relative max-w-5xl mx-auto" data-id="element-7799">
            {/* Laptop Base */}
            <div className="bg-gray-800 rounded-t-2xl sm:rounded-t-3xl p-1.5 sm:p-2 pb-0 shadow-2xl mx-auto w-full md:w-[80%]" data-id="element-7800">
              <div className="bg-white rounded-t-xl sm:rounded-t-2xl overflow-hidden h-[250px] sm:h-[300px] md:h-[500px] relative border-b-4 border-gray-100" data-id="element-7801">
                {/* Screen Content - Enhanced with realistic UI elements */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white flex" data-id="element-7802">
                  {/* Sidebar - hidden on mobile */}
                  <div className="w-12 sm:w-16 md:w-48 bg-white border-r border-gray-200 p-2 sm:p-4 hidden sm:block" data-id="element-7803">
                    {/* Logo */}
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-[#357BEB] to-[#21A366] rounded-md sm:rounded-lg mb-4 sm:mb-8 flex items-center justify-center" data-id="element-7804">
                      <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-sm" data-id="element-7805"></div>
                    </div>
                    {/* Nav Items - only visible on md+ */}
                    <div className="space-y-2 sm:space-y-3 hidden md:block" data-id="element-7806">
                      <div className="flex items-center space-x-2" data-id="element-7807">
                        <div className="w-6 h-6 bg-blue-100 rounded-lg" data-id="element-7808"></div>
                        <div className="h-2 w-20 bg-blue-500 rounded" data-id="element-7809"></div>
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-7810">
                        <div className="w-6 h-6 bg-gray-100 rounded-lg" data-id="element-7811"></div>
                        <div className="h-2 w-16 bg-gray-300 rounded" data-id="element-7812"></div>
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-7813">
                        <div className="w-6 h-6 bg-gray-100 rounded-lg" data-id="element-7814"></div>
                        <div className="h-2 w-20 bg-gray-300 rounded" data-id="element-7815"></div>
                      </div>
                    </div>
                  </div>

                  {/* Main Area - Enhanced Dashboard */}
                  <div className="flex-1 p-3 sm:p-4 md:p-6" data-id="element-7816">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-3 sm:mb-4 md:mb-6" data-id="element-7817">
                      <div data-id="element-7818">
                        <div className="h-3 w-20 sm:h-4 sm:w-32 bg-gray-800 rounded mb-1 sm:mb-2" data-id="element-7819"></div>
                        <div className="h-1.5 w-16 sm:h-2 sm:w-24 bg-gray-300 rounded" data-id="element-7820"></div>
                      </div>
                      <div className="flex items-center space-x-1.5 sm:space-x-2" data-id="element-7821">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-100 rounded-lg" data-id="element-7822"></div>
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-[#357BEB] to-[#21A366] rounded-full" data-id="element-7823"></div>
                      </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4" data-id="element-7824">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-blue-200" data-id="element-7825">
                        <div className="h-1.5 w-8 sm:h-2 sm:w-12 bg-blue-600 rounded mb-1 sm:mb-2" data-id="element-7826"></div>
                        <div className="h-3 w-10 sm:h-5 sm:w-16 bg-blue-700 rounded" data-id="element-7827"></div>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-green-200" data-id="element-7828">
                        <div className="h-1.5 w-8 sm:h-2 sm:w-12 bg-green-600 rounded mb-1 sm:mb-2" data-id="element-7829"></div>
                        <div className="h-3 w-10 sm:h-5 sm:w-16 bg-green-700 rounded" data-id="element-7830"></div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-purple-200" data-id="element-7831">
                        <div className="h-1.5 w-8 sm:h-2 sm:w-12 bg-purple-600 rounded mb-1 sm:mb-2" data-id="element-7832"></div>
                        <div className="h-3 w-10 sm:h-5 sm:w-16 bg-purple-700 rounded" data-id="element-7833"></div>
                      </div>
                    </div>

                    {/* Chart Area */}
                    <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-2 sm:p-3 md:p-4 mb-2 sm:mb-3 md:mb-4" data-id="element-7834">
                      <div className="h-1.5 w-12 sm:h-2 sm:w-20 bg-gray-700 rounded mb-2 sm:mb-3" data-id="element-7835"></div>
                      <div className="flex items-end justify-between h-16 sm:h-20 md:h-32" data-id="element-7836">
                        {[40, 65, 45, 80, 55, 90, 70].map((height, i) => <motion.div key={i} initial={{
                        height: 0
                      }} animate={{
                        height: `${height}%`
                      }} transition={{
                        delay: i * 0.1,
                        duration: 0.5
                      }} className="w-full mx-0.5 bg-gradient-to-t from-[#357BEB] to-[#21A366] rounded-t" data-id="element-7837" />)}
                      </div>
                    </div>

                    {/* Data Table */}
                    <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-2 sm:p-3" data-id="element-7838">
                      <div className="space-y-1.5 sm:space-y-2" data-id="element-7839">
                        <div className="flex items-center space-x-1.5 sm:space-x-2" data-id="element-7840">
                          <div className="w-4 h-4 sm:w-6 sm:h-6 bg-blue-100 rounded" data-id="element-7841"></div>
                          <div className="flex-1 h-1.5 sm:h-2 bg-gray-200 rounded" data-id="element-7842"></div>
                          <div className="w-8 h-1.5 sm:w-12 sm:h-2 bg-green-500 rounded" data-id="element-7843"></div>
                        </div>
                        <div className="flex items-center space-x-1.5 sm:space-x-2" data-id="element-7844">
                          <div className="w-4 h-4 sm:w-6 sm:h-6 bg-green-100 rounded" data-id="element-7845"></div>
                          <div className="flex-1 h-1.5 sm:h-2 bg-gray-200 rounded" data-id="element-7846"></div>
                          <div className="w-8 h-1.5 sm:w-12 sm:h-2 bg-blue-500 rounded" data-id="element-7847"></div>
                        </div>
                        <div className="flex items-center space-x-1.5 sm:space-x-2" data-id="element-7848">
                          <div className="w-4 h-4 sm:w-6 sm:h-6 bg-purple-100 rounded" data-id="element-7849"></div>
                          <div className="flex-1 h-1.5 sm:h-2 bg-gray-200 rounded" data-id="element-7850"></div>
                          <div className="w-8 h-1.5 sm:w-12 sm:h-2 bg-orange-500 rounded" data-id="element-7851"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Phone Overlay - Enhanced & Mobile Optimized */}
            <div className="absolute -bottom-2 right-2 sm:-bottom-4 sm:right-4 md:right-20 w-20 sm:w-24 md:w-48 bg-gray-800 rounded-[1.5rem] sm:rounded-[2rem] p-0.5 sm:p-1 shadow-2xl border-2 sm:border-4 border-gray-800" data-id="element-7852">
              <div className="bg-white rounded-[1.3rem] sm:rounded-[1.8rem] h-32 sm:h-40 md:h-80 overflow-hidden relative" data-id="element-7853">
                {/* Phone Notch */}
                <div className="absolute top-0 left-0 right-0 h-4 sm:h-6 bg-gray-50 z-10 flex justify-center" data-id="element-7854">
                  <div className="w-8 h-3 sm:w-12 sm:h-4 bg-gray-800 rounded-b-lg sm:rounded-b-xl" data-id="element-7855"></div>
                </div>

                {/* Phone Content */}
                <div className="p-2 pt-5 sm:p-3 sm:pt-8 space-y-1.5 sm:space-y-2" data-id="element-7856">
                  {/* Active Card */}
                  <motion.div initial={{
                  x: -20,
                  opacity: 0
                }} animate={{
                  x: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.3
                }} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-blue-200 sm:border-2" data-id="element-7857">
                    <div className="flex items-center space-x-1.5 sm:space-x-2 mb-1 sm:mb-2" data-id="element-7858">
                      <div className="w-4 h-4 sm:w-6 sm:h-6 bg-blue-500 rounded-md sm:rounded-lg" data-id="element-7859"></div>
                      <div className="h-1.5 w-10 sm:h-2 sm:w-16 bg-blue-600 rounded" data-id="element-7860"></div>
                    </div>
                    <div className="h-1 w-full sm:h-1.5 sm:w-full bg-blue-200 rounded mb-0.5 sm:mb-1" data-id="element-7861"></div>
                    <div className="h-1 w-2/3 sm:h-1.5 sm:w-3/4 bg-blue-200 rounded" data-id="element-7862"></div>
                  </motion.div>

                  {/* Secondary Cards */}
                  <motion.div initial={{
                  x: -20,
                  opacity: 0
                }} animate={{
                  x: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.4
                }} className="bg-gray-50 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-gray-200" data-id="element-7863">
                    <div className="flex items-center space-x-1.5 sm:space-x-2 mb-1 sm:mb-2" data-id="element-7864">
                      <div className="w-4 h-4 sm:w-6 sm:h-6 bg-gray-300 rounded-md sm:rounded-lg" data-id="element-7865"></div>
                      <div className="h-1.5 w-10 sm:h-2 sm:w-16 bg-gray-400 rounded" data-id="element-7866"></div>
                    </div>
                    <div className="h-1 w-full sm:h-1.5 sm:w-full bg-gray-200 rounded mb-0.5 sm:mb-1" data-id="element-7867"></div>
                    <div className="h-1 w-1/2 sm:h-1.5 sm:w-2/3 bg-gray-200 rounded" data-id="element-7868"></div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -left-2 top-1/4 w-12 h-12 sm:-left-4 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl sm:rounded-2xl shadow-xl opacity-20 blur-sm" data-id="element-7869" />
            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -right-2 top-1/3 w-14 h-14 sm:-right-4 sm:w-20 sm:h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-xl sm:rounded-2xl shadow-xl opacity-20 blur-sm" data-id="element-7870" />
          </motion.div>
        </div>
      </section>

      {/* 8. FINAL CTA SECTION - MOBILE OPTIMIZED */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#132C53] text-white text-center" data-id="element-7871">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7872">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-7873">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight" data-id="element-7874">
              Nu ai nevoie de experiență ca să devii agent. Ai nevoie de un
              sistem care funcționează.
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-blue-200 mb-8 sm:mb-10 px-4" data-id="element-7875">
              Iar acesta este exact ceea ce îți oferim la CautLocuință.ro.
            </p>

            <Link to="/auth/register" className="inline-flex items-center justify-center px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-bold rounded-2xl text-white bg-[#357BEB] hover:bg-[#2868d8] transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200 w-full sm:w-auto" data-id="element-7876">
              Devino Agent
            </Link>
            <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-gray-400 px-4" data-id="element-7877">
              După crearea contului, vei avea acces imediat la Training 1 și la
              ecosistemul complet CLC.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}